import React, {useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {ProviderConsoleService} from '@/modules/provider-console/services/providerConsoleService';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useAppDispatch} from '@/shared/hooks/redux';
import {setCurrentPatient} from '@/slices/patientSlice';

const Launch: React.FC = () => {
    const {login, token, isInitialized} = useAuth();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);

    const launchToken = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('token');
    }, [location.search]);

    const launchUserId = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('userId');
    }, [location.search]);

    useEffect(() => {
        let isActive = true;

        // Exchange the short-lived launch token for a session-only auth token (no persistent storage).
        const exchangeLaunchToken = async () => {
            try {
                if (!isInitialized) {
                    return;
                }

                let tokenToExchange = launchToken;
                if (!tokenToExchange) {
                    const numericUserId = Number(launchUserId);
                    if (!Number.isFinite(numericUserId) || numericUserId <= 0) {
                        setError('Missing launch token.');
                        return;
                    }

                    if (!token) {
                        setError('Unable to launch the patient dashboard. Please try again.');
                        return;
                    }

                    const launchResponse = await ProviderConsoleService.createPatientLaunchToken(numericUserId);
                    tokenToExchange = launchResponse.token;
                }

                const response = await ProviderConsoleService.exchangePatientLaunchToken(tokenToExchange);
                if (!isActive) return;

                login({
                    ...response,
                    sessionOnly: true,
                });
                const userInfo = response.userInfo as {
                    id?: number;
                    firstName?: string;
                    lastName?: string;
                    fullName?: string;
                    birthday?: string;
                };
                if (userInfo?.id && userInfo.firstName && userInfo.lastName) {
                    dispatch(
                        setCurrentPatient({
                            id: userInfo.id,
                            firstName: userInfo.firstName,
                            lastName: userInfo.lastName,
                            fullName: userInfo.fullName || `${userInfo.firstName} ${userInfo.lastName}`,
                            birthdate: userInfo.birthday || '',
                        })
                    );
                }
                navigate('/dashboard', {replace: true});
            } catch {
                if (!isActive) return;
                setError('Unable to launch the patient dashboard. Please try again.');
            }
        };

        exchangeLaunchToken();

        return () => {
            isActive = false;
        };
    }, [dispatch, isInitialized, launchToken, launchUserId, login, navigate, token]);

    if (error) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-mld-background px-6 text-center text-sm text-mld-text-primary">
                {error}
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-mld-background px-6 text-center text-sm text-mld-text-primary">
            Launching patient dashboard...
        </div>
    );
};

export default Launch;
